import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./LeaderboardModal.module.css";
import { useGame, type LeaderboardEntry } from "../store/game";
import { formatInt } from "../lib/format";

const MEDALS = ["🥇", "🥈", "🥉"];

function timeUntil(date: Date): string {
  const diff = Math.max(0, date.getTime() - Date.now());
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}д ${h % 24}г`;
  return `${h}г ${m}хв`;
}

export function LeaderboardModal() {
  const activeModal = useGame((s) => s.activeModal);
  const closeModal = useGame((s) => s.closeModal);
  const loadLeaderboard = useGame((s) => s.loadLeaderboard);
  const entries = useGame((s) => s.leaderboard);
  const myRank = useGame((s) => s.myRank);
  const nextPayout = useGame((s) => s.leaderboardNextPayout);
  const loading = useGame((s) => s.leaderboardLoading);

  const open = activeModal === "leaderboard";

  useEffect(() => {
    if (open) loadLeaderboard();
  }, [open, loadLeaderboard]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={styles.backdrop}
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />
          <motion.div
            className={styles.sheet}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 36 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) closeModal();
            }}
          >
            <div className={styles.handle} />
            <div className={styles.header}>
              <h2 className={styles.title}>🏆 Рейтинг тижня</h2>
              <p className={styles.subtitle}>Чистий прибуток за останні 7 днів</p>
              {nextPayout && (
                <div className={styles.payoutBadge}>
                  ⏱ Виплата через {timeUntil(nextPayout)} · Топ-10: 500 GT · Топ-100: 200 GT
                </div>
              )}
            </div>

            {myRank && (
              <div className={styles.myPosition}>
                Твоя позиція: #{myRank}
              </div>
            )}

            <div className={styles.list}>
              {loading ? (
                <div className={styles.loading}>Завантаження...</div>
              ) : entries.length === 0 ? (
                <div className={styles.empty}>
                  <span>🎯</span>
                  Ставок ще немає. Зроби першу ставку і потрапи в рейтинг!
                </div>
              ) : (
                entries.map((e) => <LeaderRow key={e.userId} entry={e} />)
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function LeaderRow({ entry }: { entry: LeaderboardEntry }) {
  const uid = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
  const isMe = entry.userId === uid;
  const initial = (entry.username?.[0] ?? "?").toUpperCase();

  return (
    <div className={`${styles.row} ${isMe ? styles.rowMe : ""}`}>
      <div className={styles.rank}>
        {entry.rank <= 3 ? (
          <span className={styles.medal}>{MEDALS[entry.rank - 1]}</span>
        ) : (
          entry.rank
        )}
      </div>
      <div className={styles.avatar}>{initial}</div>
      <div className={styles.nameCol}>
        <div className={styles.username}>
          {entry.username}{isMe ? " (ти)" : ""}
        </div>
        <div className={`${styles.profit} ${entry.netProfit < 0 ? styles.profitNeg : ""}`}>
          {entry.netProfit >= 0 ? "+" : ""}{formatInt(entry.netProfit)} USD
        </div>
      </div>
      {entry.dailyGt > 0 && (
        <div className={styles.gtBadge}>+{entry.dailyGt} GT/тиж</div>
      )}
    </div>
  );
}
